import React from 'react'
import { Icon, Switch, Tabs } from 'antd'
import moment from 'moment'
import Widget from '../../components/Widget'
import BeaconPeriodSelector from './BeaconPeriodSelector'
import {
  formatBeaconLatLong,
  getBeaconDeviceId,
  getBeaconMac,
  getBeaconRssi,
  getBeaconStatus,
  getBeaconTimestamp
} from './helpers'

function InfoRow({ icon, label, value }) {
  return (
    <div className="gx-media gx-align-items-center gx-flex-nowrap gx-pro-contact-list ble-beacon-info-row">
      <div className="gx-mr-3">
        <Icon type={icon} className="ble-beacon-info-icon" />
      </div>
      <div className="gx-media-body">
        <span className="gx-mb-0 gx-text-grey gx-fs-sm">{label}</span>
        <p className="gx-mb-0">{value || '-'}</p>
      </div>
    </div>
  )
}

function BleBeaconInfo({
  latestRecord,
  detectorAsset,
  detectorAssetsMap = {},
  historyData = [],
  selectedHistoryRecord,
  onSelectRecord,
  loading,
  onRefreshLatest,
  onFleetHistory,
  onClearHistory,
  isLiveFeedActive = false,
  onToggleLiveFeed,
  liveTowingRecord = null,
  lastTowDeviceAsset = null,
}) {
  const status = getBeaconStatus(latestRecord || {})
  const timestamp = latestRecord ? getBeaconTimestamp(latestRecord) : null

  return (
    <Widget styleName="gx-card-full ble-beacon-info-card">
      <Tabs defaultActiveKey="status">
        <Tabs.TabPane tab="Beacon Status" key="status">
          <div className="ble-beacon-status-body">
            {/* Live feed toggle row */}
            <div className="ble-beacon-status-header" style={{ marginBottom: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  size="small"
                  checked={isLiveFeedActive}
                  onChange={onToggleLiveFeed}
                />
                <span style={{ fontSize: 12, color: isLiveFeedActive ? '#52c41a' : '#8c8c8c', fontWeight: isLiveFeedActive ? 600 : 400 }}>
                  {isLiveFeedActive ? 'Live Feed On' : 'Start Live Feed'}
                </span>
                {isLiveFeedActive && (
                  <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 2 }}>
                    {loading ? 'Refreshing…' : '· every 10s'}
                  </span>
                )}
              </span>
            </div>
            {isLiveFeedActive && <div className="ble-live-progress" />}

            <InfoRow
              icon="check-circle"
              label="Status"
              value={
                <span style={{
                  fontWeight: 600,
                  color: status === 'online' ? '#52c41a' : status === '-' ? '#8c8c8c' : '#f5222d',
                  textTransform: 'capitalize',
                }}>
                  {status}
                </span>
              }
            />
            <InfoRow icon="clock-circle" label="Time" value={timestamp ? moment(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-'} />
            <InfoRow icon="wifi" label="RSSI Value" value={getBeaconRssi(latestRecord || {})} />
            <InfoRow icon="environment" label="Lat/Lng" value={formatBeaconLatLong(latestRecord || {})} />
            <InfoRow icon="laptop" label="Detector Asset/Equipment" value={
              <span>
                {detectorAsset ? detectorAsset.display : '-'}
                {getBeaconDeviceId(latestRecord || {}) !== '-' && (
                  <span style={{ display: 'block', fontSize: 11, opacity: 0.55, lineHeight: 1.3 }}>
                    {getBeaconDeviceId(latestRecord || {})}
                  </span>
                )}
              </span>
            } />
            <InfoRow icon="barcode" label="BLE MAC ID" value={getBeaconMac(latestRecord || {})} />
            <InfoRow icon="thunderbolt" label="Battery" value={
              (() => {
                const r = latestRecord || {}
                const mv = r.batteryMv || r.battery_mv || r.batterymv
                return mv != null ? `${mv} mV` : '-'
              })()
            } />

            {/* Live towing status — persists after live feed is toggled off */}
            {liveTowingRecord && (
              <div style={{ marginTop: 12 }}>
                <hr style={{ margin: '8px 0' }} />
                {liveTowingRecord.isTowing && liveTowingRecord.towingRecord ? (
                  <div style={{
                    background: 'linear-gradient(135deg, #fff2e8 0%, #fff7f0 100%)',
                    border: '1.5px solid #fa8c16',
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                      <span style={{ fontSize: 15 }}>🚨</span>
                      <span style={{ fontWeight: 700, color: '#d46b08', fontSize: 13 }}>
                        Currently Being Towed
                      </span>
                    </div>
                    {(() => {
                      const t = liveTowingRecord.towingRecord
                      const towAsset = (detectorAssetsMap && t.deviceId && detectorAssetsMap[t.deviceId]) || lastTowDeviceAsset || null
                      return (
                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                          {towAsset ? (
                            <div style={{ marginBottom: 4 }}>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>{towAsset.display || towAsset.assetCode || '—'}</div>
                              <div style={{ color: '#8c8c8c', fontSize: 11, fontFamily: 'monospace' }}>{t.deviceId}</div>
                            </div>
                          ) : (
                            <div>
                              <span style={{ color: '#8c8c8c' }}>Device: </span>
                              <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{t.deviceId || '—'}</span>
                            </div>
                          )}
                          {t.activeDisplacementM > 0 && (
                            <div>
                              <span style={{ color: '#8c8c8c' }}>Displaced: </span>
                              <span style={{ fontWeight: 600, color: '#d46b08' }}>{Math.round(t.activeDisplacementM)} m</span>
                            </div>
                          )}
                          {t.towingSinceTs && (
                            <div>
                              <span style={{ color: '#8c8c8c' }}>Since: </span>
                              <span>{moment(Number(t.towingSinceTs)).format('DD/MM HH:mm:ss')}</span>
                            </div>
                          )}
                          {t.locationAtEvent && (
                            <div style={{ marginTop: 3, fontSize: 11, color: '#aaa' }}>
                              📍 {t.locationAtEvent}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                ) : (
                  (() => {
                    const lr = liveTowingRecord.latestRecord
                    const lastTs = liveTowingRecord.lastUpdatedAt
                    if (lr && lastTs) {
                      // A record exists but it's stale or non-TOWING — show last tow info
                      return (
                        <div style={{
                          background: '#fffbe6',
                          border: '1.5px solid #ffe58f',
                          borderRadius: 8,
                          padding: '10px 12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                            <Icon type="history" style={{ color: '#faad14', fontSize: 14 }} />
                            <span style={{ fontWeight: 700, color: '#ad6800', fontSize: 12 }}>Last Tow Detected</span>
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#ad6800', background: '#fff1b8', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>
                              {lr.status}
                            </span>
                          </div>
                          {(() => {
                            const towAsset = (detectorAssetsMap && lr.deviceId && detectorAssetsMap[lr.deviceId]) || lastTowDeviceAsset || null
                            return (
                              <div style={{ fontSize: 12, lineHeight: 1.9 }}>
                                {towAsset ? (
                                  <div style={{ marginBottom: 4 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{towAsset.display || towAsset.assetCode || '—'}</div>
                                    <div style={{ color: '#8c8c8c', fontSize: 11, fontFamily: 'monospace' }}>{lr.deviceId}</div>
                                  </div>
                                ) : (
                                  <div>
                                    <span style={{ color: '#8c8c8c' }}>Device: </span>
                                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{lr.deviceId || '—'}</span>
                                  </div>
                                )}
                                <div>
                                  <span style={{ color: '#8c8c8c' }}>Detected at: </span>
                                  <span>{moment(lastTs).format('DD/MM/YY HH:mm:ss')}</span>
                                </div>
                                {lr.activeDisplacementM > 0 && (
                                  <div>
                                    <span style={{ color: '#8c8c8c' }}>Displacement: </span>
                                    <span>{Math.round(lr.activeDisplacementM)} m</span>
                                  </div>
                                )}
                                {lr.locationAtEvent && (
                                  <div style={{ marginTop: 2, fontSize: 11, color: '#aaa' }}>📍 {lr.locationAtEvent}</div>
                                )}
                              </div>
                            )
                          })()}
                        </div>
                      )
                    }
                    // No records at all
                    return (
                      <div style={{
                        background: '#f6ffed',
                        border: '1.5px solid #b7eb8f',
                        borderRadius: 8,
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                        fontSize: 12,
                        color: '#389e0d',
                        fontWeight: 600,
                      }}>
                        <Icon type="check-circle" style={{ color: '#52c41a' }} />
                        No towing history found
                      </div>
                    )
                  })()
                )}
              </div>
            )}
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Fleet History" key="history">
          <BeaconPeriodSelector
            loading={loading}
            onShow={onFleetHistory}
            onClear={onClearHistory}
            historyData={historyData}
            detectorAssetsMap={detectorAssetsMap}
            selectedRecord={selectedHistoryRecord}
            onRowClick={onSelectRecord}
          />
        </Tabs.TabPane>
      </Tabs>
    </Widget>
  )
}

export default BleBeaconInfo
